import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { ThemeProvider } from '@mui/system'
import { browserTracingIntegration, init } from '@sentry/react'

import App from 'app/app'
import Auth0ProviderWithHistory from 'app/auth/Auth0ProviderWithHistory'
import ProtectedRoute from 'app/auth/ProtectedRoute'
import { persistor, store } from 'app/store'
import Modals from 'common/components/Modal'
import Snackbars from 'common/components/Snackbars'
import { isLocalhost } from 'common/utils/envUtils'
import { APP_VERSION } from 'common/utils/versionConstants'

import LoginLandingPage from 'features/Auth/LoginLandingPage'

import { muiLighthouseTheme } from 'styles/muiTheme'

import 'common/utils/chart/setup'
import 'styles/index.scss'

const conditionalMockWrapper = async () => {
  // Edit this to toggle mocking on/off
  const enableMock = false

  if (!enableMock) {
    return
  }

  const { startMockServer } = await import('mocks/browser')

  return startMockServer()
}

conditionalMockWrapper().then(() => {
  // consider adding replayIntegration() if things get hard to diagnose
  init({
    dsn: import.meta.env.SENTRY_DSN,
    integrations: [browserTracingIntegration()],
    beforeBreadcrumb(breadcrumb) {
      // Filter out debug level console logs
      if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
        return null
      }

      return breadcrumb
    },
    tracesSampleRate: isLocalhost() ? 1.0 : 0.5,
    tracePropagationTargets: [
      'localhost',
      import.meta.env.LH_TIMP_API_BASE_URL
    ],
    environment: import.meta.env.LH_TIMP_ENV_NAME,
    // set release only for cloud builds; default to commit hashes for local debugging
    release: isLocalhost()
      ? undefined
      : `LH.TIMP@${APP_VERSION}+${import.meta.env.LH_TIMP_BUILD_VERSION}`
  })

  const container = document.getElementById('root')
  const root = createRoot(container as HTMLElement)
  // if we want to stay compliant with v7, we need to set v7_relativeSplatPath to true
  // and update all places where we match paths
  const future = {
    v7_startTransition: true,
    v7_relativeSplatPath: false
  }

  root.render(
    <StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ThemeProvider theme={muiLighthouseTheme}>
            <Router future={future}>
              <Auth0ProviderWithHistory>
                <Routes>
                  <Route path='/login' element={<LoginLandingPage />} />
                  <Route
                    path='*'
                    element={
                      <ProtectedRoute>
                        <App />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
                <Modals />
              </Auth0ProviderWithHistory>
              <Snackbars />
            </Router>
            <ToastContainer />
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </StrictMode>
  )
})
